import * as _ from 'lodash-es';
import { computed, ref } from 'vue';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { DirectImportConnection, Integration, JournalEntry } from '@repo/core/models';
import { useDirectImportConnectionService, useDirectImportService } from '@repo/core/services';
import { useDxConfirm, useDxEventBus } from '@repo/vue/utils';
import { useClientStore } from '@/stores';
import { EventConstants } from '@repo/core/constants';

export const useDirectImportStore = defineStore('directImport', () => {
  // services
  const directImportService = useDirectImportService();
  const directImportConnectionService = useDirectImportConnectionService();

  // utils
  const dxConfirm = useDxConfirm();
  const dxEventBus = useDxEventBus();

  // stores
  const clientStore = useClientStore();

  // state
  const companies = ref<any[]>([]);
  const sessionStatus = ref<any>();
  const connection = ref<DirectImportConnection>();
  const integrations = ref<Integration[]>([]);

  // getters
  const connected = computed(() => !_.isNil(connection.value?.companyId));
  const connectedIntegration = computed(() => {
    return _.find(integrations.value, (x) => {
      return !connection.value ? false : x.id === connection.value?.integrationId;
    });
  });
  const xeroConnectionBeta = computed(() => !!clientStore.currentClient?.directImportBeta);

  // actions
  const canConnect = (integrationId: string | undefined) => {
    return _.toUpper(integrationId) == 'E48824AD-1CB4-4192-A317-F6BB15850BF0' ? xeroConnectionBeta.value : true;
  };

  const canDrillDown = () => {
    return (
      connected.value &&
      canConnect(connection.value?.directImportConnectionId) &&
      _.toUpper(connection.value?.integrationId) !== 'E48824AD-1CB4-4192-A317-F6BB15850BF0' &&
      _.toUpper(connection.value?.integrationId) !== '141ED04F-7B27-41D6-80E8-F1C54DF295AB'
    );
  };

  const fetchIntegrations = async () => {
    const result = await directImportService.getIntegrations();
    integrations.value = result;
    return result;
  };

  const fetchClientConnection = async () => {
    const result = await directImportConnectionService.getClientConnection();
    connection.value = result;
    return result;
  };

  const disconnect = async (companyId?: string | undefined) => {
    if (connection.value?.id) {
      await directImportConnectionService.deleteConnections([connection.value.id]);
    }
    await directImportService.disconnect(connection.value?.directImportConnectionId, companyId);
    connection.value = {} as DirectImportConnection;

    const payload: any = {
      type: 'directImports',
      status: 'disconnected',
      companyId: companyId
    };
    dxEventBus.emit(EventConstants.directImportChanged, payload);
  };

  const openDirectImports = async (integration: Integration) => {
    if (connected.value && connectedIntegration.value?.id && !canConnect(integration.id)) {
      const result = await dxConfirm.confirmation(
        'Confirm',
        `This client is connected to <strong>${connectedIntegration.value?.name}</strong>. Do you want to disconnect from <strong>${connectedIntegration.value?.name}</strong>?`,
        'Yes',
        'Cancel'
      );
      if (result) {
        await disconnect();
        return true;
      } else {
        return false;
      }
    } else if (!canConnect(integration?.id)) {
      return true;
    }

    if (connectedIntegration.value && connection.value && connection.value?.integrationId !== integration.id) {
      // TODO: Find a way to localize store messages
      const result = await dxConfirm.confirmation(
        'Confirm',
        `This client is already connected to <strong>${connectedIntegration.value?.name}</strong>. Do you want to disconnect from <strong>${connectedIntegration.value?.name}</strong> and connect to <strong>${integration?.name}</strong> instead?`,
        'Yes',
        'Cancel'
      );
      if (result) {
        await disconnect();
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  const validateConnection = async () => {
    try {
      return directImportService.validate(connection.value?.directImportConnectionId, connection.value?.companyId);
    } catch (error: any) {
      console.error(error);
      await disconnect();
    }
  };

  const createConnection = async (newConnection: DirectImportConnection) => {
    try {
      await directImportConnectionService.post([newConnection]);
      connection.value = newConnection;

      if (!connection.value?.expiry) {
        delete connection.value?.expiry;
      }

      const payload: any = {
        type: 'directImports',
        status: 'connected'
      };
      dxEventBus.emit(EventConstants.directImportChanged, payload);
    } catch (err: any) {
      console.error(err);
      await disconnect();
    }
  };

  const login = (
    integrationId: string | undefined,
    country: string | undefined,
    identifier: string | undefined,
    fields: any,
    authMethod?: string
  ) => {
    const persist =
      _.toUpper(integrationId) == 'E48824AD-1CB4-4192-A317-F6BB15850BF0' ? xeroConnectionBeta.value : true;
    return directImportService.login(integrationId, country, identifier, persist, fields, authMethod);
  };

  const fetchCompanies = async (sessionId: string | undefined) => {
    const result = await directImportService.getCompanies(sessionId);
    companies.value = result;
    return result;
  };

  const fetchTrialBalanceEntries = (
    sessionId: string | undefined,
    company: string | undefined,
    index: string | undefined,
    monthly: string | undefined,
    autoMap: boolean | undefined,
    removeEmptyAccounts: boolean | undefined,
    openingBalanceCheck: boolean | undefined,
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    return directImportService.getTrialBalanceEntries(
      sessionId,
      company,
      index,
      monthly,
      autoMap,
      removeEmptyAccounts,
      openingBalanceCheck,
      startDate,
      endDate
    );
  };

  const fetchStatus = async (sessionId: string | undefined) => {
    const result = await directImportService.getStatus(sessionId);
    sessionStatus.value = result;
    return result;
  };

  const fetchGeneralLedgerEntries = (sessionId: string | undefined, data: any) => {
    return directImportService.getGeneralLedgerEntries(sessionId, data);
  };

  const postJournals = (sessionId: string | undefined, companyId: string | undefined, journals: JournalEntry[]) => {
    return directImportService.postJournals(sessionId, companyId, journals);
  };

  const saveAutoLink = (country: string, index: string, data: any) => {
    return directImportService.autoLink(country, index, data);
  };

  const state = { companies, sessionStatus, connection, integrations };
  const getters = { connected, connectedIntegration, xeroConnectionBeta };
  const actions = {
    canConnect,
    canDrillDown,
    fetchIntegrations,
    fetchClientConnection,
    disconnect,
    openDirectImports,
    validateConnection,
    createConnection,
    login,
    fetchCompanies,
    fetchStatus,
    fetchGeneralLedgerEntries,
    fetchTrialBalanceEntries,
    postJournals,
    saveAutoLink
  };

  return { ...state, ...getters, ...actions };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDirectImportStore, import.meta.hot));
}
